import { useTranslation } from '@/hooks/useTranslation';
import { formatMoney } from '@/lib/utils';
import { TrendingUp, Target, Calendar } from 'lucide-react';

interface YearlyGoalCardProps {
  yearlyGoal: number;
  totalSavings: number;
  currency: string;
  displayCurrency?: string;
}

export function YearlyGoalCard({
  yearlyGoal,
  totalSavings,
  currency,
  displayCurrency,
}: YearlyGoalCardProps) {
  const { t } = useTranslation();
  
  // If no goal set, show message
  if (!yearlyGoal || yearlyGoal === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white/80 p-6 shadow-xl backdrop-blur-xl dark:border-slate-700 dark:bg-slate-800/80">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-pink-600">
            <Target className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
            {t('settings.yearlyGoal')}
          </h3>
        </div>
        
        <div className="text-center py-8">
          <p className="text-slate-600 dark:text-slate-400 mb-2">
            {t('dashboard.noDataYet')}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-500">
            {t('settings.yearlyGoalSubtitle')}
          </p>
        </div>
      </div>
    );
  }
  
  // Calculate progress
  const progress = Math.min((totalSavings / yearlyGoal) * 100, 100);
  const remaining = Math.max(yearlyGoal - totalSavings, 0);
  const exceeded = totalSavings > yearlyGoal;
  const exceedAmount = totalSavings - yearlyGoal;
  
  // Determine color based on progress
  let progressColor = 'from-red-500 to-pink-600'; // 0-33%
  let progressBg = 'bg-red-100 dark:bg-red-900/20';
  let statusEmoji = '🔴';
  let statusText = t('common.loading');
  
  if (progress >= 100) {
    progressColor = 'from-blue-500 to-purple-600';
    progressBg = 'bg-blue-100 dark:bg-blue-900/20';
    statusEmoji = '🎉';
    statusText = exceeded 
      ? `${t('dashboard.goal')} ${t('common.exceeded')}!`
      : `${t('dashboard.goal')} ${t('common.achieved')}!`;
  } else if (progress >= 67) {
    progressColor = 'from-green-500 to-emerald-600';
    progressBg = 'bg-green-100 dark:bg-green-900/20';
    statusEmoji = '🟢';
    statusText = t('dashboard.almostThere') || 'Almost there!';
  } else if (progress >= 34) {
    progressColor = 'from-yellow-500 to-orange-600';
    progressBg = 'bg-yellow-100 dark:bg-yellow-900/20';
    statusEmoji = '🟡';
    statusText = t('dashboard.goodProgress') || 'Good progress!';
  } else {
    statusText = t('dashboard.keepGoing') || 'Keep going!';
  }
  
  // Calculate months remaining (current month to December)
  const currentMonth = new Date().getMonth(); // 0-11
  const monthsRemaining = 12 - currentMonth;
  const monthlyTarget = monthsRemaining > 0 ? remaining / monthsRemaining : 0;
  
  // Display currency (if different from account)
  const showDualCurrency = displayCurrency && displayCurrency !== currency;
  
  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white/80 to-purple-50/50 p-6 shadow-xl backdrop-blur-xl dark:border-slate-700 dark:from-slate-800/80 dark:to-purple-900/20">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${progressColor}`}>
            <Target className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">
              {t('settings.yearlyGoal')}
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {new Date().getFullYear()}
            </p>
          </div>
        </div>
        
        <div className={`rounded-full px-3 py-1 ${progressBg}`}>
          <span className="text-sm font-semibold">
            {statusEmoji} {statusText}
          </span>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            {t('dashboard.yearlyProgress')}
          </span>
          <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
            {progress.toFixed(1)}%
          </span>
        </div>
        
        <div className="relative h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
          <div
            className={`h-full rounded-full bg-gradient-to-r ${progressColor} transition-all duration-500 ease-out`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      </div>
      
      {/* Amounts */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-xs text-slate-600 dark:text-slate-400 mb-1">
            {t('dashboard.totalSavings')}
          </p>
          <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
            {formatMoney(totalSavings, currency)}
          </p>
          {showDualCurrency && (
            <p className="text-xs text-slate-500 mt-0.5">
              ≈ {formatMoney(totalSavings, displayCurrency)}
            </p>
          )}
        </div>
        
        <div>
          <p className="text-xs text-slate-600 dark:text-slate-400 mb-1">
            {t('dashboard.goal')}
          </p>
          <p className="text-xl font-bold text-slate-900 dark:text-slate-100">
            {formatMoney(yearlyGoal, currency)}
          </p>
          {showDualCurrency && (
            <p className="text-xs text-slate-500 mt-0.5">
              ≈ {formatMoney(yearlyGoal, displayCurrency)}
            </p>
          )}
        </div>
      </div>
      
      {/* Status Messages */}
      {exceeded ? (
        <div className="rounded-xl bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">🎉</div>
            <div className="flex-1">
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                Congratulations! Goal exceeded!
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                You saved {formatMoney(exceedAmount, currency)} more than your goal!
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Remaining Amount */}
          <div className="flex items-center justify-between rounded-xl bg-slate-50 dark:bg-slate-700/50 p-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-slate-600 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Remaining
              </span>
            </div>
            <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
              {formatMoney(remaining, currency)}
            </span>
          </div>
          
          {/* Monthly Target */}
          {monthsRemaining > 0 && (
            <div className="flex items-center justify-between rounded-xl bg-slate-50 dark:bg-slate-700/50 p-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                  Need per month ({monthsRemaining} months left)
                </span>
              </div>
              <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                {formatMoney(monthlyTarget, currency)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}